import { Fragment, useCallback, useMemo, useState } from 'react'

import { useForecast, useForecastConfidence } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { fmt, STATUS_BG_COLORS } from './utils'
import type { ForecastConfidenceRow, WeeklyForecastRow } from './types'

type RowType = 'currency' | 'badge' | 'headroom'

interface RowDef {
  key: string
  label: string
  type: RowType
  bold?: boolean
}

const RECEIPTS_ROWS: RowDef[] = [
  { key: 'ar_collections_current', label: 'AR Collections Current', type: 'currency' },
  { key: 'ar_collections_1_30', label: 'AR 1-30', type: 'currency' },
  { key: 'ar_collections_31_60', label: 'AR 31-60', type: 'currency' },
  { key: 'ar_collections_61_90', label: 'AR 61-90', type: 'currency' },
  { key: 'ar_collections_91_plus', label: 'AR 91+', type: 'currency' },
  { key: 'contractual_revenue', label: 'Contractual Revenue', type: 'currency' },
  { key: 'other_receipts', label: 'Other Receipts', type: 'currency' },
  { key: 'total_receipts', label: 'Total Receipts', type: 'currency', bold: true },
]

const COMMITTED_ROWS: RowDef[] = [
  { key: 'payroll', label: 'Payroll', type: 'currency' },
  { key: 'debt_service', label: 'Debt Service', type: 'currency' },
  { key: 'rent_leases', label: 'Rent and Leases', type: 'currency' },
  { key: 'taxes_insurance', label: 'Taxes and Insurance', type: 'currency' },
  { key: 'total_committed', label: 'Total Committed', type: 'currency', bold: true },
]

const DISCRETIONARY_ROWS: RowDef[] = [
  { key: 'ap_payments', label: 'AP Payments', type: 'currency' },
  { key: 'capex', label: 'CapEx', type: 'currency' },
  { key: 'other_disbursements', label: 'Other', type: 'currency' },
  { key: 'total_discretionary', label: 'Total Discretionary', type: 'currency', bold: true },
]

const CASHFLOW_ROWS: RowDef[] = [
  { key: 'total_disbursements', label: 'Total Disbursements', type: 'currency', bold: true },
  { key: 'net_cash_flow', label: 'Net Cash Flow', type: 'currency', bold: true },
  { key: 'beginning_cash', label: 'Beginning Cash', type: 'currency' },
  { key: 'ending_cash', label: 'Ending Cash', type: 'currency', bold: true },
]

const FACILITY_ROWS: RowDef[] = [
  { key: 'revolver_draw', label: 'Revolver Draw', type: 'currency' },
  { key: 'revolver_repayment', label: 'Revolver Repayment', type: 'currency' },
  { key: 'revolver_outstanding', label: 'Revolver Outstanding', type: 'currency' },
  { key: 'available_credit', label: 'Available Credit', type: 'currency' },
]

const STATUS_ROWS: RowDef[] = [
  { key: 'liquidity_status', label: 'Liquidity Status', type: 'badge' },
  { key: 'covenant_status', label: 'Covenant Status', type: 'badge' },
  { key: 'covenant_headroom', label: 'Covenant Headroom', type: 'headroom' },
]

interface SectionDef {
  id: string
  title: string
  headerBgClass: string
  headerLabelClass: string
  rows: RowDef[]
}

const SECTIONS: SectionDef[] = [
  {
    id: 'receipts',
    title: 'Receipts',
    headerBgClass: 'bg-teal-50/80 dark:bg-teal-950/20',
    headerLabelClass: 'text-teal-900 dark:text-teal-200 border-l-4 border-teal-500',
    rows: RECEIPTS_ROWS,
  },
  {
    id: 'committed',
    title: 'Committed Disbursements',
    headerBgClass: 'bg-orange-50/80 dark:bg-orange-950/20',
    headerLabelClass:
      'text-orange-900 dark:text-orange-200 border-l-4 border-orange-500',
    rows: COMMITTED_ROWS,
  },
  {
    id: 'discretionary',
    title: 'Discretionary',
    headerBgClass: 'bg-slate-100/90 dark:bg-slate-900/30',
    headerLabelClass:
      'text-slate-800 dark:text-slate-200 border-l-4 border-slate-500',
    rows: DISCRETIONARY_ROWS,
  },
  {
    id: 'cashflow',
    title: 'Cash Flow',
    headerBgClass: 'bg-amber-50/80 dark:bg-amber-950/20',
    headerLabelClass: 'text-amber-900 dark:text-amber-200 border-l-4 border-amber-500',
    rows: CASHFLOW_ROWS,
  },
  {
    id: 'facility',
    title: 'Facility',
    headerBgClass: 'bg-slate-50 dark:bg-slate-900/20',
    headerLabelClass:
      'text-slate-800 dark:text-slate-200 border-l-4 border-slate-400',
    rows: FACILITY_ROWS,
  },
  {
    id: 'status',
    title: 'Status',
    headerBgClass: 'bg-gray-50 dark:bg-gray-900/20',
    headerLabelClass: 'text-gray-800 dark:text-gray-200 border-l-4 border-gray-400',
    rows: STATUS_ROWS,
  },
]

const SUMMABLE_KEYS: (keyof WeeklyForecastRow)[] = [
  'ar_collections_current',
  'ar_collections_1_30',
  'ar_collections_31_60',
  'ar_collections_61_90',
  'ar_collections_91_plus',
  'contractual_revenue',
  'other_receipts',
  'total_receipts',
  'payroll',
  'debt_service',
  'rent_leases',
  'taxes_insurance',
  'total_committed',
  'ap_payments',
  'capex',
  'other_disbursements',
  'total_discretionary',
  'total_disbursements',
  'net_cash_flow',
  'revolver_draw',
  'revolver_repayment',
]

const LAST_WEEK_KEYS: (keyof WeeklyForecastRow)[] = [
  'beginning_cash',
  'ending_cash',
  'revolver_outstanding',
  'available_credit',
  'liquidity_status',
  'covenant_status',
  'covenant_headroom',
]

interface MonthlyAgg extends Record<string, unknown> {
  month: string
  weeks_included: number
}

function normalizeLineItem(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
}

function monthKeyFromWeekEnding(weekEnding: string): string {
  return weekEnding.slice(0, 7)
}

function aggregateMonthly(weeks: WeeklyForecastRow[]): MonthlyAgg[] {
  const buckets: Record<string, WeeklyForecastRow[]> = {}
  for (const week of weeks) {
    const monthKey = monthKeyFromWeekEnding(week.week_ending)
    if (!buckets[monthKey]) buckets[monthKey] = []
    buckets[monthKey].push(week)
  }

  return Object.keys(buckets)
    .sort()
    .map(month => {
      const monthWeeks = buckets[month].sort((a, b) =>
        a.week_ending.localeCompare(b.week_ending),
      )
      const first = monthWeeks[0]
      const last = monthWeeks[monthWeeks.length - 1]
      const aggregate: MonthlyAgg = { month, weeks_included: monthWeeks.length }
      aggregate.beginning_cash = first.beginning_cash
      for (const key of SUMMABLE_KEYS) {
        aggregate[key as string] = monthWeeks.reduce(
          (sum, row) => sum + (Number(row[key]) || 0),
          0,
        )
      }
      for (const key of LAST_WEEK_KEYS) {
        if (key !== 'beginning_cash') aggregate[key as string] = last[key]
      }
      return aggregate
    })
}

function endingCashRowClass(status: string): string {
  const normalized = (status || '').toUpperCase()
  if (normalized === 'GREEN') return 'bg-emerald-50 dark:bg-emerald-950/10'
  if (normalized === 'YELLOW') return 'bg-amber-50 dark:bg-amber-950/10'
  if (normalized === 'RED') return 'bg-red-50 dark:bg-red-950/10'
  return ''
}

function headroomClass(value: number): string {
  if (value < 5_000_000) return 'text-red-600'
  if (value < 10_000_000) return 'text-amber-600'
  return 'text-emerald-600'
}

function currencyColor(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return 'text-gray-400'
  if (value < 0) return 'text-red-600'
  return 'text-gray-900 dark:text-gray-100'
}

export default function ForecastGridPage() {
  const forecastQuery = useForecast()
  const { data: weeks = [], isLoading, isError, error, refetch } = forecastQuery
  const { data: confidenceRows = [] } = useForecastConfidence()

  const [view, setView] = useState<'weekly' | 'monthly'>('weekly')
  const [showConfidence, setShowConfidence] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggleSection = useCallback((id: string) => {
    setCollapsed(previous => ({ ...previous, [id]: !previous[id] }))
  }, [])

  const monthlyAggregated = useMemo(() => aggregateMonthly(weeks), [weeks])

  const columns = useMemo(() => {
    if (view === 'weekly') {
      return weeks.map(week => ({
        key: String(week.week),
        labelTop: `W${week.week}`,
        labelSub: fmt.weekLabel(week.week_ending),
        weekNum: week.week,
        weekEnding: week.week_ending,
      }))
    }

    return monthlyAggregated.map(month => {
      const date = new Date(`${month.month}-01T12:00:00`)
      return {
        key: month.month,
        labelTop: date.toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        }),
        labelSub: `${month.weeks_included} wk`,
        weekNum: null as number | string | null,
        weekEnding: null as string | null,
      }
    })
  }, [monthlyAggregated, view, weeks])

  const confidenceMap = useMemo(() => {
    const map = new Map<string, ForecastConfidenceRow>()
    for (const row of confidenceRows) {
      const lineItem = normalizeLineItem(row.line_item)
      map.set(`${row.week}|${lineItem}`, row)
      map.set(`${row.week_ending}|${lineItem}`, row)
    }
    return map
  }, [confidenceRows])

  const getConfidence = useCallback(
    (weekNum: number | string, weekEnding: string, rowKey: string) => {
      const normalized = normalizeLineItem(rowKey)
      return (
        confidenceMap.get(`${weekNum}|${normalized}`) ??
        confidenceMap.get(`${weekEnding}|${normalized}`)
      )
    },
    [confidenceMap],
  )

  const cellValue = useCallback(
    (rowKey: string, colKey: string): number | string | undefined => {
      if (view === 'weekly') {
        const week = weeks.find(row => String(row.week) === colKey)
        return week
          ? ((week as unknown as Record<string, unknown>)[rowKey] as
              | number
              | string
              | undefined)
          : undefined
      }
      const month = monthlyAggregated.find(row => row.month === colKey)
      return month
        ? ((month as unknown as Record<string, unknown>)[rowKey] as
            | number
            | string
            | undefined)
        : undefined
    },
    [monthlyAggregated, view, weeks],
  )

  const colLiquidityStatus = useCallback(
    (colKey: string): string => {
      const value = cellValue('liquidity_status', colKey)
      return typeof value === 'string' ? value : ''
    },
    [cellValue],
  )

  if (isLoading) {
    return <CashflowV2Loading label="Loading forecast…" />
  }

  if (isError) {
    if (looksUnconfigured(error)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load forecast"
        description={(error as Error)?.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          13-Week Forecast Grid
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Rolling liquidity matrix by week or calendar month from receipts through
          covenant headroom.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface px-4 py-3 shadow-sm">
        <div className="inline-flex rounded-md border border-gray-200 dark:border-dark-border p-0.5 bg-gray-50 dark:bg-dark-surface-2">
          {(['weekly', 'monthly'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setView(mode)}
              className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
                view === mode
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {mode === 'weekly' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>

        <label
          className={`flex cursor-pointer items-center gap-2 text-sm ${
            view === 'monthly'
              ? 'cursor-not-allowed text-gray-400'
              : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            checked={showConfidence && view === 'weekly'}
            disabled={view === 'monthly'}
            onChange={event => setShowConfidence(event.target.checked)}
          />
          Show confidence (P10–P90)
        </label>
      </div>

      <div className="overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface shadow-sm max-h-[calc(100vh-260px)]">
        <table className="min-w-max w-full border-collapse text-sm">
          <thead>
            <tr>
              <th
                className="sticky left-0 z-30 min-w-[220px] border-b border-r border-gray-200 dark:border-dark-border bg-gray-100 dark:bg-dark-surface-2 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                scope="col"
              >
                Line item
              </th>
              {columns.map(column => (
                <th
                  key={column.key}
                  scope="col"
                  className="sticky top-0 z-20 min-w-[120px] border-b border-gray-200 dark:border-dark-border bg-gray-100 dark:bg-dark-surface-2 px-3 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300"
                >
                  <div>{column.labelTop}</div>
                  <div className="mt-0.5 text-[10px] font-normal text-gray-400 dark:text-gray-500">
                    {column.labelSub}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map(section => {
              const isCollapsed = collapsed[section.id] ?? false
              const visibleRows = section.rows.filter(row => !isCollapsed || row.bold)

              return (
                <Fragment key={section.id}>
                  <tr className="border-b border-gray-100 dark:border-dark-border">
                    <td
                      className={`sticky left-0 z-20 min-w-[220px] cursor-pointer select-none border-b border-r border-gray-200 dark:border-dark-border px-4 py-2.5 text-xs font-bold uppercase tracking-wide ${section.headerBgClass} ${section.headerLabelClass}`}
                      onClick={() => toggleSection(section.id)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          toggleSection(section.id)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="mr-2 inline-block w-3 text-center text-gray-500 dark:text-gray-400">
                        {isCollapsed ? '▸' : '▾'}
                      </span>
                      {section.title}
                    </td>
                    {columns.map(column => (
                      <td
                        key={column.key}
                        className={`border-b border-gray-200 dark:border-dark-border px-3 py-2.5 ${section.headerBgClass}`}
                        aria-hidden
                      />
                    ))}
                  </tr>
                  {visibleRows.map(row => (
                    <tr
                      key={`${section.id}-${row.key}`}
                      className="border-b border-gray-50 dark:border-dark-border hover:bg-gray-50/60 dark:hover:bg-dark-surface-2/40"
                    >
                      <th
                        scope="row"
                        className={`sticky left-0 z-10 border-r border-gray-100 dark:border-dark-border bg-white dark:bg-dark-surface px-4 py-2 text-left font-normal text-gray-700 dark:text-gray-200 ${
                          row.bold ? 'font-semibold text-gray-900 dark:text-gray-100' : ''
                        }`}
                      >
                        {row.label}
                      </th>
                      {columns.map(column => {
                        const value = cellValue(row.key, column.key)
                        const status = colLiquidityStatus(column.key)

                        if (row.type === 'badge') {
                          const badgeValue = typeof value === 'string' ? value : ''
                          const badge =
                            STATUS_BG_COLORS[badgeValue.toUpperCase()] ??
                            'bg-gray-100 text-gray-700'
                          return (
                            <td
                              key={column.key}
                              className="whitespace-nowrap border-l border-gray-50 dark:border-dark-border px-3 py-2 text-center align-middle"
                            >
                              {badgeValue ? (
                                <span
                                  className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${badge}`}
                                >
                                  {badgeValue}
                                </span>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>
                          )
                        }

                        if (row.type === 'headroom') {
                          const numeric = typeof value === 'number' ? value : undefined
                          return (
                            <td
                              key={column.key}
                              className={`whitespace-nowrap border-l border-gray-50 dark:border-dark-border px-3 py-2 text-right font-mono tabular-nums ${
                                numeric != null ? headroomClass(numeric) : 'text-gray-400'
                              }`}
                            >
                              {numeric != null ? fmt.compact(numeric) : '—'}
                            </td>
                          )
                        }

                        const numeric = typeof value === 'number' ? value : undefined
                        const isEnding = row.key === 'ending_cash'
                        const isNet = row.key === 'net_cash_flow'
                        const endingBg = isEnding ? endingCashRowClass(status) : ''
                        const textColor =
                          isNet && numeric != null
                            ? numeric < 0
                              ? 'text-red-600'
                              : 'text-emerald-600'
                            : numeric != null
                              ? isEnding
                                ? numeric < 0
                                  ? 'text-red-600'
                                  : 'text-gray-900 dark:text-gray-100'
                                : currencyColor(numeric)
                              : 'text-gray-400'

                        let confBand: string | null = null
                        if (
                          showConfidence &&
                          view === 'weekly' &&
                          column.weekNum != null &&
                          column.weekEnding
                        ) {
                          const confidence = getConfidence(
                            column.weekNum,
                            column.weekEnding,
                            row.key,
                          )
                          if (confidence) {
                            confBand = `${fmt.compact(confidence.p10)} – ${fmt.compact(
                              confidence.p90,
                            )}`
                          }
                        }

                        return (
                          <td
                            key={column.key}
                            className={`whitespace-nowrap border-l border-gray-50 dark:border-dark-border px-3 py-2 text-right font-mono text-xs tabular-nums ${row.bold ? 'font-semibold' : ''} ${endingBg} ${textColor}`}
                          >
                            <div>{numeric != null ? fmt.compact(numeric) : '—'}</div>
                            {confBand && (
                              <div className="mt-0.5 text-[10px] font-normal text-gray-400 dark:text-gray-500">
                                {confBand}
                              </div>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
